import { Link } from 'react-router-dom';
import { formatSlotLabel, type TeacherSummary } from '@musicville/shared';
import { Icon } from '@/components/brand/Icon';
import { MediaFrame } from '@/components/ui/MediaFrame';
import { cn } from '@/lib/cn';
import { useI18n } from '@/lib/i18n';

export function TeacherCard({
  teacher,
  className,
}: {
  teacher: TeacherSummary;
  className?: string | undefined;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  const featured = teacher.featured;

  return (
    <article
      className={cn(
        'flex flex-col overflow-hidden rounded-lg',
        featured
          ? 'bg-midnight-raised text-on-midnight'
          : 'border border-border-subtle bg-surface shadow-card',
        className,
      )}
    >
      <MediaFrame
        ratio="portrait"
        src={teacher.photoUrl}
        alt={teacher.fullName}
        className="w-full"
      />

      <div className="flex flex-1 flex-col gap-3 p-5">
        <h3 className={cn('text-h4 font-semibold', featured ? 'text-on-midnight' : 'text-primary')}>
          {teacher.fullName}
        </h3>
        <p className={cn('text-body-sm', featured ? 'text-on-midnight-muted' : 'text-secondary')}>
          {teacher.headline}
        </p>

        <p
          className={cn(
            'flex items-center gap-1.5 text-caption font-medium',
            featured ? 'text-on-midnight-accent' : 'text-accent',
          )}
        >
          <Icon name="clock" size={16} />
          {teacher.nextAvailableAt
            ? `${t('teachers.nextAvailable')}: ${formatSlotLabel(teacher.nextAvailableAt, locale)}`
            : t('teachers.noAvailability')}
        </p>

        <Link
          to={`/teachers/${teacher.slug}`}
          className={cn(
            'mt-auto inline-flex h-11 w-full items-center justify-center rounded-md text-body font-medium transition-colors',
            featured
              ? 'bg-action text-on-accent hover:bg-action-hover'
              : 'border border-border-strong bg-surface text-primary hover:bg-subtle',
          )}
        >
          {t('teachers.viewProfile')}
        </Link>
      </div>
    </article>
  );
}

export function TeacherCardSkeleton({ className }: { className?: string }): React.JSX.Element {
  return (
    <div
      className={cn('overflow-hidden rounded-lg border border-border-subtle bg-surface', className)}
    >
      <div className="aspect-[320/380] w-full animate-pulse bg-border-subtle" />
      <div className="flex flex-col gap-3 p-5">
        <div className="h-6 w-2/3 animate-pulse rounded-sm bg-border-subtle" />
        <div className="h-4 w-1/2 animate-pulse rounded-sm bg-border-subtle" />
        <div className="h-4 w-3/5 animate-pulse rounded-sm bg-border-subtle" />
        <div className="mt-2 h-11 w-full animate-pulse rounded-md bg-border-subtle" />
      </div>
    </div>
  );
}
